import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { nextElement } from '@uplift/api';
import { FormElement, NextBehavior } from '@uplift/types';
import { ContextualNavigationHeaderProps, ContextualNavigationLayout, Markdown } from '@uplift/ui-v2';
import { useFormikContext } from 'formik';
import { useHistory, useParams } from 'react-router';
import { FormFooter, useFormLayoutStyles } from '../layout';
import { useInitializedRenderFormContext } from './render-form.context';
import { FormType, RenderFormRouteParams } from '../types';
import { ElementRenderer, ReplacedFooter } from '../elements';
import { getAnswerValue, getPrevious, getRequest } from '../helpers';
import { useL10n } from '@uplift/hooks';

export type RenderElementProps = {
  element: FormElement;
  isPreview?: boolean;
};

export const RenderElement: React.VFC<RenderElementProps> = ({ element, isPreview }) => {
  const { state, dispatch } = useInitializedRenderFormContext();
  const { groups } = state;
  const { page } = useParams<RenderFormRouteParams>();
  const l10n = useL10n();
  const history = useHistory();
  const formik = useFormikContext<FormType>();
  const { classes } = useFormLayoutStyles();

  const push = useCallback((url: string) => {
    history.push(url);
  }, [ history ]);

  useEffect(() => {
    formik.validateField(element.formElementId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!formik.values[element.formElementId] && state.answerVal) {
      formik.setFieldValue(element.formElementId, state.answerVal);
    }
  }, [ state.answerVal ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = useCallback(async () => {
    if (isPreview) {
      push(`/form/${state.form.formId}/preview/${Number(page) - 1}`);
      return;
    }

    await getPrevious(state.submissionId ?? '', element, dispatch, push);
  }, [ dispatch, element, isPreview, page, push, state.form.formId, state.submissionId ]);

  const handleExit = useCallback(() => {
    push('/');
  }, [ push ]);

  const headerProps: ContextualNavigationHeaderProps = useMemo(() => {
    const hProps: ContextualNavigationHeaderProps = {
      rightButtonText: l10n.navigation.exit,
      onRightButtonClick: handleExit
    };

    if (Number(page) > 1) {
      hProps.leftButtonText = l10n.navigation.back;
      hProps.onLeftButtonClick = handleBack;
    }

    return hProps;
  }, [ handleBack, handleExit, l10n.navigation.back, l10n.navigation.exit, page ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [ element ]);

  const calculatedProgress = element.isFinal
    ? 100
    : element.orderNumber * 100 / state.totalElements;

  const handleClick = () => {
    handleUpload();
  };

  const handleSingleSelect = (value?: string | null) => {
    handleUpload(value);
  };

  const handleUpload = async (value?: string | null) => {
    if (isPreview) {
      push(`/form/${state.form.formId}/preview/${Number(page) + 1}`);
      return;
    }

    dispatch({
      type: 'getElement'
    });

    const result = await nextElement({
      ...getRequest(state?.submissionId ?? '', element, formik, value ?? undefined),
      next_behavior: state.nextBehavior
    });

    if (element.isFinal && element.buttonLink) {
      history.push(element.buttonLink);
      return;
    }

    if ('error' in result) {
      dispatch({
        type: 'setError',
        payload: result.error
      });

      return;
    }

    dispatch({
      type: 'setElement',
      payload: {
        element: result.element,
        shouldRefetch: state.nextBehavior === NextBehavior.SkipToLast,
        answerVal: result.response ? getAnswerValue(result.element, result.response) : undefined,
      }
    });

    if (!(result.element.formElementId in formik.values)) {
      formik.setFieldValue(result.element.formElementId, null);
    }

    push(`/form/${state.submissionId}/page/${result.element.orderNumber}`);
  };

  const handleCancel = () => {
    dispatch({
      type: 'cancelEdit'
    });

    if (state.reviewPageElement) {
      push(`/form/${state.submissionId}/page/${state.reviewPageElement.orderNumber}`);
    }

    handleUpload();
  };

  const group = groups.find(x => x.formGroupId === element.formGroupId)?.name;
  const elementType = element.formElementDefinition.formElementType;
  const replaceFooter = element.formElementAnswers.some(x => x.placement === 'footer');

  return (
    <ContextualNavigationLayout
      headerProps={headerProps}
      progress={calculatedProgress}
    >
      <Box className={classes.root}>
        {elementType !== 'divider' && (
          <Box mb={3}>
            <Typography variant="overline" color="secondary">
              {group}
            </Typography>
          </Box>
        )}
        <Box mb={3}>
          <Typography component="div">
            <Markdown>{element.elementText}</Markdown>
            <Box mt={1}>
              <Markdown>{element.hintText ?? ''}</Markdown>
            </Box>
          </Typography>
        </Box>
        <Box>
          <ElementRenderer
            element={element}
            isPreview={isPreview ?? false}
            onSingleSelect={handleSingleSelect}
          />
        </Box>
        {!replaceFooter && (
          <FormFooter
            nextBehavior={state.nextBehavior}
            buttonText={element.buttonText ?? ''}
            showBack={!element.isFinal && page !== '1' && elementType !== 'review_page'}
            showNext={![ 'single_select', 'scale' ].includes(elementType)}
            isFinal={element.isFinal ?? false}
            disabledRight={Boolean(formik.errors[element.formElementId])}
            onBack={handleBack}
            onCancel={handleCancel}
            onClick={handleClick}
          />
        )}
        {replaceFooter && (
          <ReplacedFooter element={element} onClick={handleSingleSelect} />
        )}
      </Box>
    </ContextualNavigationLayout>
  );
};
